import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { isAdminEmail } from './adminAccess';
import { filterActive, softDelete } from './trash';
import { replaceTemplateVariables, TEMPLATE_CATEGORIES, TEMPLATE_SIDES } from './templates';
import {
  CASE_CATEGORY_TO_TOP_FILTER,
  getAllMatterTypeIds,
  getDefaultStages,
  getMatterTypeDisplayLabel,
  getMatterTypeForCase,
  inferCategoryFromMatterType,
  resolveMatterTypeId,
  resolvePartyLabelsForCase,
  type CaseTaxonomyContextInput,
} from './caseTaxonomyRegistry';
import {
  CRIMINAL_CASE_TAXONOMY_CONFIG,
  resolveLegacyCriminalProceeding,
} from './criminalTaxonomy';
import {
  findCriminalProceedingById,
  getProceedingsForCategory,
  getSelectableProceedingsForCategory,
  LEGACY_CRIMINAL_TYPE_MAP,
} from './criminalTaxonomyHelpers';
import {
  getCriminalCategoryIdForSubFilter,
  matchesTemplateTaxonomy,
  SUBFILTER_OPTIONS,
  TOP_FILTER_OPTIONS,
  type TopFilter,
} from './caseTaxonomyFilter';
import { normalizeStageLabel } from './stageAliases';
import {
  resolveCaseStagesList,
  resolveCurrentCaseStage,
  type CaseStageContextInput,
} from './caseStages';

/** Case-type key → workflow stages where this template is recommended */
export type TemplateStagesByCaseType = Record<string, string[]>;

export type SystemTemplateTab = { id: string; title: string; content: string };

export interface SystemTemplateDoc {
  id: string;
  title?: string;
  content?: string;
  tabs?: SystemTemplateTab[];
  activeTabId?: string;
  type?: string;
  isSystem?: boolean;
  templateCaseTypes?: string[];
  templateSides?: string[];
  templateStagesByCaseType?: TemplateStagesByCaseType;
  documentType?: string;
  updatedAt?: unknown;
  createdAt?: unknown;
}

const CASE_TYPE_KEYS = new Set(getAllMatterTypeIds());

/** Normalize case side to plaintiff | defendant for template matching */
export function normalizeTemplateSide(
  side: string | undefined | null,
): 'plaintiff' | 'defendant' | null {
  if (!side) return null;
  const s = side.toLowerCase().trim();
  if (
    s === 'plaintiff' ||
    s === 'petitioner' ||
    s === 'appellant' ||
    s === 'applicant' ||
    s === 'party a' ||
    s === 'wife' ||
    s === 'decree holder' ||
    s === 'complainant' ||
    s === 'state' ||
    s === 'prosecution' ||
    s === 'prosecutor'
  ) {
    return 'plaintiff';
  }
  if (
    s === 'defendant' ||
    s === 'respondent' ||
    s === 'judgment debtor' ||
    s === 'party b' ||
    s === 'husband' ||
    s === 'accused'
  ) {
    return 'defendant';
  }
  return null;
}

/** Resolve matter type id used in templateCaseTypes (taxonomy keys). */
export function resolveCaseTypeKey(caseData: Record<string, unknown> | null | undefined): string {
  if (!caseData) return 'Civil Suit';
  const raw =
    (caseData.matterType as string) ||
    (caseData.caseMatter as string) ||
    (caseData.caseType as string) ||
    '';
  const resolved = resolveMatterTypeId(raw);
  if (resolved && CASE_TYPE_KEYS.has(resolved)) return resolved;
  const fromCase = getMatterTypeForCase({
    caseCategory: caseData.caseCategory as string | undefined,
    matterType: raw,
    caseType: caseData.caseType as string | undefined,
    category: caseData.category as string | undefined,
  });
  if (fromCase) return fromCase.id;
  return 'Civil Suit';
}

/** Derive template categorization fields from a linked case (or empty for unlinked). */
export function buildTemplateMetadataFromCase(
  caseData: Record<string, unknown> | null | undefined,
): {
  templateCaseTypes: string[];
  templateSides: string[];
  templateStagesByCaseType: TemplateStagesByCaseType;
  categoryHint: string;
} {
  if (!caseData) {
    return {
      templateCaseTypes: [],
      templateSides: [],
      templateStagesByCaseType: {},
      categoryHint: 'Uncategorized / Other (no case linked)',
    };
  }

  const caseTypeKey = resolveCaseTypeKey(caseData);
  const side = normalizeTemplateSide(
    (caseData.representingSide as string) || (caseData.sideRepresented as string),
  );
  const typeLabel = getMatterTypeDisplayLabel(caseTypeKey);
  const sideLabel =
    side === 'plaintiff' ? 'Plaintiff' : side === 'defendant' ? 'Defendant' : null;
  const categoryHint = sideLabel ? `${typeLabel} · ${sideLabel} side` : typeLabel;

  const defaultStages = getDefaultStages({
    caseCategory: caseData.caseCategory as string | undefined,
    matterType:
      (caseData.matterType as string) ||
      (caseData.caseMatter as string) ||
      (caseData.caseType as string),
    caseType: caseData.caseType as string | undefined,
    category: caseData.category as string | undefined,
  });

  return {
    templateCaseTypes: [caseTypeKey],
    templateSides: side ? [side] : [],
    templateStagesByCaseType:
      defaultStages.length > 0 ? { [caseTypeKey]: defaultStages } : {},
    categoryHint,
  };
}

export function templateMatchesCase(
  tpl: Pick<SystemTemplateDoc, 'templateCaseTypes' | 'templateSides'>,
  caseData: Record<string, unknown> | null | undefined,
): boolean {
  const caseTypeKey = resolveCaseTypeKey(caseData);
  const side = normalizeTemplateSide(
    (caseData?.representingSide as string) || (caseData?.sideRepresented as string),
  );

  const types = tpl.templateCaseTypes;
  const criminalCategory = caseData?.criminalCategory as string | undefined;
  const typesOk =
    !types?.length ||
    types.some((t) => {
      const resolved = resolveMatterTypeId(t);
      if (t === caseTypeKey || resolved === caseTypeKey) return true;
      if (criminalCategory) {
        const legacy = LEGACY_CRIMINAL_TYPE_MAP[t];
        if (legacy?.categoryId === criminalCategory) return true;
      }
      return false;
    });

  const sides = tpl.templateSides;
  const sidesOk = !sides?.length || (side != null && sides.includes(side));

  return typesOk && sidesOk;
}

function buildTaxonomyContextForTemplateMatterType(
  matterTypeId: string,
): CaseTaxonomyContextInput {
  const resolved = resolveMatterTypeId(matterTypeId);
  const category = inferCategoryFromMatterType(resolved || matterTypeId);
  const ctx: CaseTaxonomyContextInput = {
    caseCategory: category,
    matterType: resolved || matterTypeId,
    caseType: resolved || matterTypeId,
  };

  if (category === 'Criminal') {
    const legacy =
      resolveLegacyCriminalProceeding(matterTypeId) ??
      resolveLegacyCriminalProceeding(resolved) ??
      LEGACY_CRIMINAL_TYPE_MAP[matterTypeId] ??
      LEGACY_CRIMINAL_TYPE_MAP[resolved];
    if (legacy) {
      ctx.criminalCategory = legacy.categoryId;
      ctx.proceedingType = legacy.proceedingId;
      ctx.matterType = legacy.proceedingId;
      ctx.caseType = legacy.proceedingId;
    }
  }

  return ctx;
}

/** Side badges for the templates library (plaintiff/defendant → taxonomy party plurals). */
export function getTemplateSideBadgeLabels(
  tpl: Pick<
    SystemTemplateDoc,
    'templateCaseTypes' | 'templateStagesByCaseType' | 'templateSides'
  >,
): { role: 'plaintiff' | 'defendant'; label: string }[] {
  const rawSides = tpl.templateSides?.filter(
    (s): s is 'plaintiff' | 'defendant' => s === 'plaintiff' || s === 'defendant',
  );
  if (!rawSides?.length) return [];

  const matterIds = resolveTemplateMatterTypeIds(tpl);
  const labels = matterIds[0]
    ? resolvePartyLabelsForCase(buildTaxonomyContextForTemplateMatterType(matterIds[0]))
    : null;

  const order: ('plaintiff' | 'defendant')[] = ['plaintiff', 'defendant'];
  const uniqueSides = [...new Set(rawSides)].sort(
    (a, b) => order.indexOf(a) - order.indexOf(b),
  );

  return uniqueSides.map((role) => ({
    role,
    label:
      role === 'plaintiff'
        ? labels?.party1Plural ?? 'Plaintiffs'
        : labels?.party2Plural ?? 'Defendants',
  }));
}

/** Whether a template has explicit stage picks (vs case-stage fallbacks). */
export function templateHasExplicitStageConfiguration(
  tpl: Pick<SystemTemplateDoc, 'templateStagesByCaseType'>,
): boolean {
  return Object.values(tpl.templateStagesByCaseType ?? {}).some(
    (stages) => Array.isArray(stages) && stages.length > 0,
  );
}

export function templateMatchesCaseForRecommendations(
  tpl: Pick<
    SystemTemplateDoc,
    'templateCaseTypes' | 'templateSides' | 'templateStagesByCaseType'
  >,
  caseData: Record<string, unknown> | null | undefined,
): boolean {
  if (!caseData) return false;
  if (!templateMatchesCase(tpl, caseData)) return false;
  return resolveRecommendedStagesForTemplate(tpl, caseData).length > 0;
}

/** HTML + optional tab structure from a system or workspace template document. */
function templateUpdatedAtMs(tpl: Pick<SystemTemplateDoc, 'updatedAt'>): number {
  const u = tpl.updatedAt;
  if (!u) return 0;
  if (typeof u === 'object' && u !== null && 'toMillis' in u) {
    return (u as { toMillis: () => number }).toMillis();
  }
  if (typeof u === 'object' && u !== null && 'seconds' in u) {
    return (u as { seconds: number }).seconds * 1000;
  }
  return 0;
}

/** True when a stored title is really a Firestore doc id / slug, not a display name. */
export function isSystemTemplateSlugTitle(value: string | undefined | null): boolean {
  const t = (value || '').trim().toLowerCase();
  if (!t) return true;
  if (t.startsWith('system-template-')) return true;
  return !/\s/.test(t) && t.includes('-');
}

/** Human-readable title for library, tabs, and recommendations (never a raw doc id). */
export function getDisplayTemplateTitle(
  tpl: Pick<SystemTemplateDoc, 'title' | 'id' | 'tabs'>,
): string {
  const raw = (tpl.title || '').trim();
  if (raw && !isSystemTemplateSlugTitle(raw)) return raw;

  const tabTitle = tpl.tabs?.find((tab) => tab.title?.trim() && !isSystemTemplateSlugTitle(tab.title))
    ?.title;
  if (tabTitle?.trim()) return tabTitle.trim();

  if (tpl.id.startsWith('system-template-')) {
    return tpl.id
      .slice('system-template-'.length)
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return raw || tpl.id;
}

function slugBodyFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[\s/]+/g, '-')
    .replace(/[^a-z0-9&-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function hashTitleKey(norm: string): string {
  let h = 5381;
  for (let i = 0; i < norm.length; i++) {
    h = ((h << 5) + h) ^ norm.charCodeAt(i);
  }
  return Math.abs(h).toString(36);
}

function templateSlugFromDoc(tpl: Pick<SystemTemplateDoc, 'id' | 'title' | 'tabs'>): string {
  if (tpl.id.startsWith('system-template-')) {
    return tpl.id.slice('system-template-'.length);
  }
  return slugBodyFromTitle(getDisplayTemplateTitle(tpl as SystemTemplateDoc));
}

/** Whether the display title was inferred from a doc id slug, not typed by an admin. */
export function isTitleDerivedFromDocId(
  tpl: Pick<SystemTemplateDoc, 'id' | 'title' | 'tabs'>,
): boolean {
  if (isSystemTemplateSlugTitle(tpl.title || '')) return true;
  if (!tpl.id.startsWith('system-template-')) return false;
  const fromId = tpl.id
    .slice('system-template-'.length)
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const display = getDisplayTemplateTitle(tpl as SystemTemplateDoc);
  return (
    normalizeTemplateTitle(display) === normalizeTemplateTitle(fromId) ||
    normalizeTemplateTitle(display).startsWith(normalizeTemplateTitle(fromId))
  );
}

function templateContentScore(tpl: Pick<SystemTemplateDoc, 'content' | 'tabs'>): number {
  const tabs = tpl.tabs ?? [];
  if (tabs.length > 0) {
    return tabs.reduce((sum, tab) => sum + (tab.content?.length ?? 0), 0);
  }
  return (tpl.content?.length ?? 0);
}

/** True when the in-memory library row already has body text (skip refetch). */
export function templateHasFullContent(tpl: SystemTemplateDoc): boolean {
  return templateContentScore(tpl) > 0;
}

const TEMPLATE_COPY_TITLE_SUFFIX = /\s*\(copy(?:\s+\d+)?\)\s*$/i;

export function isTemplateCopyTitle(title: string): boolean {
  return TEMPLATE_COPY_TITLE_SUFFIX.test(title.trim());
}

/**
 * Same Firestore template — NOT "Plaint" vs "Plaint & Application…",
 * and NOT "Bail Petition for BBA" vs "Bail Petition for BA".
 * Only exact titles, exact slug ids, or legacy truncated-id pairs for the same title.
 */
export function templatesAreLogicalDuplicates(
  a: Pick<SystemTemplateDoc, 'id' | 'title' | 'tabs'>,
  b: Pick<SystemTemplateDoc, 'id' | 'title' | 'tabs'>,
): boolean {
  if (a.id === b.id) return true;

  const displayA = getDisplayTemplateTitle(a as SystemTemplateDoc);
  const displayB = getDisplayTemplateTitle(b as SystemTemplateDoc);

  const titleA = normalizeTemplateTitle(displayA);
  const titleB = normalizeTemplateTitle(displayB);
  if (titleA && titleB && titleA === titleB) return true;

  const slugA = templateSlugFromDoc(a);
  const slugB = templateSlugFromDoc(b);
  if (slugA && slugB && slugA === slugB) return true;

  // Legacy: Firestore doc id truncated to 120 chars for the same title only.
  if (titleA && titleB && titleA === titleB && slugA && slugB && slugA !== slugB) {
    const longer = slugA.length >= slugB.length ? slugA : slugB;
    const shorter = slugA.length < slugB.length ? slugA : slugB;
    if (longer.startsWith(shorter) && longer.length - shorter.length <= 8) {
      return true;
    }
  }

  return false;
}

export function preferTemplateRecord(
  current: SystemTemplateDoc,
  candidate: SystemTemplateDoc,
): SystemTemplateDoc {
  const currentHuman = !isSystemTemplateSlugTitle(current.title || '');
  const candidateHuman = !isSystemTemplateSlugTitle(candidate.title || '');
  if (candidateHuman && !currentHuman) return normalizeTemplateRecord(candidate);
  if (currentHuman && !candidateHuman) return normalizeTemplateRecord(current);

  const currentDerived = isTitleDerivedFromDocId(current);
  const candidateDerived = isTitleDerivedFromDocId(candidate);
  if (candidateDerived && !currentDerived) return normalizeTemplateRecord(candidate);
  if (currentDerived && !candidateDerived) return normalizeTemplateRecord(current);

  const titleLenA = getDisplayTemplateTitle(current).length;
  const titleLenB = getDisplayTemplateTitle(candidate).length;
  if (titleLenB !== titleLenA) {
    return titleLenB > titleLenA
      ? normalizeTemplateRecord(candidate)
      : normalizeTemplateRecord(current);
  }

  const scoreA = templateContentScore(current);
  const scoreB = templateContentScore(candidate);
  if (scoreB !== scoreA) {
    return scoreB > scoreA
      ? normalizeTemplateRecord(candidate)
      : normalizeTemplateRecord(current);
  }

  return templateUpdatedAtMs(candidate) > templateUpdatedAtMs(current)
    ? normalizeTemplateRecord(candidate)
    : normalizeTemplateRecord(current);
}

/** Group templates that share the same logical identity (exact title / slug). */
export function buildTemplateDuplicateClusters(
  all: SystemTemplateDoc[],
): SystemTemplateDoc[][] {
  const clusters: SystemTemplateDoc[][] = [];
  const assigned = new Set<string>();

  for (const tpl of all) {
    if (assigned.has(tpl.id)) continue;
    const cluster = all.filter(
      (t) => !assigned.has(t.id) && templatesAreLogicalDuplicates(t, tpl),
    );
    cluster.forEach((t) => assigned.add(t.id));
    clusters.push(cluster);
  }

  return clusters;
}

function clusterSystemTemplates(all: SystemTemplateDoc[]): SystemTemplateDoc[][] {
  return buildTemplateDuplicateClusters(all);
}

/** Pick the canonical library entry (card + editor) for a template cluster. */
export function pickCanonicalLibraryTemplate(
  templates: SystemTemplateDoc[],
  ref: Pick<SystemTemplateDoc, 'id' | 'title' | 'tabs'>,
): SystemTemplateDoc {
  const cluster = templates.filter((t) => templatesAreLogicalDuplicates(t, ref));
  if (cluster.length === 0) {
    return normalizeTemplateRecord(ref as SystemTemplateDoc);
  }
  return cluster.reduce(preferTemplateRecord);
}

/**
 * Optional repair: merge only true duplicates (same title / truncated slug pair).
 * Surviving doc is the one with the most editor content.
 */
export async function consolidateSystemTemplateDuplicates(): Promise<number> {
  const all = (await fetchAllSystemTemplateDocs()).map(normalizeTemplateRecord);
  const clusters = clusterSystemTemplates(all);
  let removed = 0;

  for (const cluster of clusters) {
    if (cluster.length <= 1) continue;

    const richest = cluster.reduce((a, b) =>
      templateContentScore(b) > templateContentScore(a) ? b : a,
    );
    const titleSource =
      cluster.find(
        (t) =>
          !isSystemTemplateSlugTitle(t.title || '') && !isTitleDerivedFromDocId(t),
      ) ?? richest;

    const payload = normalizeTemplateSavePayload({
      title: getDisplayTemplateTitle(titleSource),
      content: richest.content ?? '',
      tabs: richest.tabs,
      activeTabId: richest.activeTabId,
      templateCaseTypes: titleSource.templateCaseTypes ?? richest.templateCaseTypes,
      templateSides: titleSource.templateSides ?? richest.templateSides,
      templateStagesByCaseType:
        titleSource.templateStagesByCaseType ?? richest.templateStagesByCaseType,
      documentType: titleSource.documentType ?? richest.documentType,
    });

    const keeperId = richest.id;
    await upsertLexGatSystemTemplate(payload, keeperId);

    for (const t of cluster) {
      if (t.id === keeperId) continue;
      await deleteDoc(doc(db, 'workspaces', 'SYSTEM', 'documents', t.id));
      removed += 1;
    }
  }

  return removed;
}

/** LexGat templates for the admin Templates tab (same dedupe rules as case recommendations). */
export async function fetchSystemTemplatesForAdminLibrary(): Promise<SystemTemplateDoc[]> {
  return fetchSystemTemplates();
}

/** Fix in-memory title when Firestore stored a slug/id by mistake. */
export function normalizeTemplateRecord(tpl: SystemTemplateDoc): SystemTemplateDoc {
  const displayTitle = getDisplayTemplateTitle(tpl);
  if (displayTitle && displayTitle !== tpl.title) {
    return { ...tpl, title: displayTitle };
  }
  return tpl;
}

/** Title to persist on save — never a URL slug or doc id. */
export function resolveLexGatTemplateSaveTitle(
  editorTitle: string,
  urlSlugOrType?: string,
): string {
  const fromEditor = editorTitle.trim();
  if (fromEditor && !isSystemTemplateSlugTitle(fromEditor)) return fromEditor;
  if (urlSlugOrType?.trim() && !isSystemTemplateSlugTitle(urlSlugOrType)) {
    return urlSlugOrType.trim();
  }
  return fromEditor || 'Untitled Template';
}

/** One logical LexGat template — prefers human title + newest content. */
export function dedupeSystemTemplatesByTitle(
  templates: SystemTemplateDoc[],
): SystemTemplateDoc[] {
  const sorted = [...templates].sort(
    (a, b) => templateUpdatedAtMs(b) - templateUpdatedAtMs(a),
  );
  const kept: SystemTemplateDoc[] = [];

  for (const tpl of sorted) {
    const normalized = normalizeTemplateRecord(tpl);
    const idx = kept.findIndex((k) => templatesAreLogicalDuplicates(k, normalized));
    if (idx === -1) {
      kept.push(normalized);
      continue;
    }
    kept[idx] = preferTemplateRecord(kept[idx], normalized);
  }

  return kept;
}

/** Align legacy `content` with the active tab before writing to Firestore. */
export function normalizeTemplateSavePayload(
  input: LexGatTemplateUpsertInput,
): LexGatTemplateUpsertInput {
  const resolved = resolveTemplateContent({
    content: input.content ?? '',
    tabs: input.tabs,
    activeTabId: input.activeTabId,
  });
  const tabs =
    resolved.tabs ??
    input.tabs?.map((t, i) => ({
      id: t.id || `tab-${i + 1}`,
      title: t.title || `Document ${i + 1}`,
      content: t.content ?? '',
    }));
  return {
    ...input,
    content: resolved.html,
    ...(tabs?.length
      ? { tabs, activeTabId: resolved.activeTabId ?? input.activeTabId ?? tabs[0]?.id }
      : {}),
  };
}

export function resolveTemplateContent(template: Pick<SystemTemplateDoc, 'content' | 'tabs' | 'activeTabId'>): {
  html: string;
  tabs?: SystemTemplateTab[];
  activeTabId?: string;
} {
  const tabs = template.tabs?.filter((t) => t && typeof t.content === 'string');
  if (tabs && tabs.length > 0) {
    const activeId = template.activeTabId || tabs[0]?.id;
    const active = tabs.find((t) => t.id === activeId) ?? tabs[0];
    return {
      html: active?.content ?? '',
      tabs: tabs.map((t, i) => ({
        id: t.id || `tab-${i + 1}`,
        title: t.title || `Document ${i + 1}`,
        content: t.content ?? '',
      })),
      activeTabId: active?.id || tabs[0]?.id,
    };
  }
  return { html: template.content ?? '' };
}

/** Apply case/user variables to template HTML and every tab. */
export function applyCaseVariablesToTemplate(
  template: Pick<SystemTemplateDoc, 'content' | 'tabs' | 'activeTabId'>,
  caseData: Record<string, unknown> | null | undefined,
  userData: unknown,
): ReturnType<typeof resolveTemplateContent> {
  const resolved = resolveTemplateContent(template);
  const tabs = resolved.tabs?.map((tab) => ({
    ...tab,
    content: replaceTemplateVariables(tab.content ?? '', caseData, userData),
  }));
  const activeId = resolved.activeTabId || tabs?.[0]?.id;
  const active = tabs?.find((t) => t.id === activeId) ?? tabs?.[0];
  const html = replaceTemplateVariables(resolved.html, caseData, userData);
  return {
    html: active?.content ?? html,
    ...(tabs?.length ? { tabs, activeTabId: activeId } : {}),
  };
}

/** Keys that may appear in templateStagesByCaseType for one taxonomy matter type. */
function matterTypeKeysForTemplateLookup(matterTypeId: string): string[] {
  const resolved = resolveMatterTypeId(matterTypeId);
  const keys = new Set<string>();
  if (resolved) keys.add(resolved);
  if (matterTypeId?.trim()) keys.add(matterTypeId.trim());
  const label = getMatterTypeDisplayLabel(resolved || matterTypeId);
  if (label && label !== resolved) keys.add(label);
  return [...keys];
}

/** Stages configured for a template on a specific case type (merges legacy + taxonomy keys). */
export function getTemplateStagesForCaseType(
  tpl: Pick<SystemTemplateDoc, 'templateStagesByCaseType'>,
  caseTypeKey: string,
): string[] {
  const map = tpl.templateStagesByCaseType;
  if (!map) return [];
  const merged: string[] = [];
  const seen = new Set<string>();
  for (const key of matterTypeKeysForTemplateLookup(caseTypeKey)) {
    for (const stage of map[key] ?? []) {
      const norm = stage.trim().toLowerCase();
      if (!norm || seen.has(norm)) continue;
      seen.add(norm);
      merged.push(stage);
    }
  }
  return merged;
}

function caseDataToStageContext(
  caseData: Record<string, unknown>,
): Parameters<typeof normalizeStageLabel>[1] {
  return {
    caseCategory: caseData.caseCategory as string | undefined,
    matterType: (caseData.matterType || caseData.caseType || caseData.caseMatter) as
      | string
      | undefined,
    caseType: caseData.caseType as string | undefined,
    category: caseData.category as string | undefined,
  };
}

export function resolveRecommendedStagesForTemplate(
  tpl: Pick<SystemTemplateDoc, 'templateStagesByCaseType'>,
  caseData: Record<string, unknown> | null | undefined,
): string[] {
  if (!caseData) return [];

  const ctx = caseDataToStageContext(caseData);
  const canonicalize = (stages: string[]): string[] => {
    const out: string[] = [];
    for (const stage of stages) {
      const canonical = normalizeStageLabel(stage, ctx);
      if (!canonical) continue;
      if (!out.some((s) => s.toLowerCase() === canonical.toLowerCase())) {
        out.push(canonical);
      }
    }
    return out;
  };

  const forCaseType = canonicalize(
    getTemplateStagesForCaseType(tpl, resolveCaseTypeKey(caseData)),
  );
  if (forCaseType.length > 0) return forCaseType;

  const allConfigured: string[] = [];
  const seen = new Set<string>();
  for (const stages of Object.values(tpl.templateStagesByCaseType ?? {})) {
    for (const stage of stages ?? []) {
      const norm = stage.trim().toLowerCase();
      if (!norm || seen.has(norm)) continue;
      seen.add(norm);
      allConfigured.push(stage);
    }
  }
  const fromAnyCaseType = canonicalize(allConfigured);
  if (fromAnyCaseType.length > 0) return fromAnyCaseType;

  const enriched = caseData as CaseStageContextInput;
  const current = resolveCurrentCaseStage(enriched);
  if (current?.trim()) {
    const fromCurrent = canonicalize([current]);
    if (fromCurrent.length > 0) return fromCurrent;
  }

  const caseStages = resolveCaseStagesList(enriched);
  if (caseStages.length > 0) {
    const fromList = canonicalize([caseStages[0]]);
    if (fromList.length > 0) return fromList;
  }

  return canonicalize(['Other']);
}

export function getSystemTemplatesForCase(
  caseData: Record<string, unknown> | null | undefined,
  systemTemplates: SystemTemplateDoc[],
): SystemTemplateDoc[] {
  return systemTemplates
    .filter((t) => templateMatchesCase(t, caseData))
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
}

export function getWorkspaceTemplatesForCase(
  caseData: Record<string, unknown> | null | undefined,
  workspaceTemplates: SystemTemplateDoc[],
): SystemTemplateDoc[] {
  return workspaceTemplates
    .filter((t) => templateMatchesCase(t, caseData))
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
}

/** Infer templateCaseTypes from stage map keys or TEMPLATE_CATEGORIES. */
export function inferTemplateCaseTypes(
  template: Pick<SystemTemplateDoc, 'title' | 'templateStagesByCaseType' | 'documentType'>,
): string[] {
  const fromStages = new Set<string>();
  for (const rawKey of Object.keys(template.templateStagesByCaseType ?? {})) {
    const resolved = resolveMatterTypeId(rawKey);
    if (resolved && CASE_TYPE_KEYS.has(resolved)) fromStages.add(resolved);
    else if (CASE_TYPE_KEYS.has(rawKey)) fromStages.add(rawKey);
  }
  if (fromStages.size > 0) return [...fromStages];

  const docType = template.documentType || template.title || '';
  return Object.entries(TEMPLATE_CATEGORIES)
    .filter(([, types]) => types.includes(docType))
    .map(([category]) => category)
    .filter((cat) => CASE_TYPE_KEYS.has(cat));
}

export function inferTemplateSides(docType: string): string[] {
  return TEMPLATE_SIDES[docType] || [];
}

async function fetchAllSystemTemplateDocs(): Promise<SystemTemplateDoc[]> {
  const sysDocsRef = collection(db, 'workspaces', 'SYSTEM', 'documents');
  let sysSnap;
  try {
    sysSnap = await getDocs(
      query(
        sysDocsRef,
        where('isSystem', '==', true),
        where('type', '==', 'template'),
        orderBy('updatedAt', 'desc'),
        limit(200),
      ),
    );
  } catch {
    sysSnap = await getDocs(
      query(
        sysDocsRef,
        where('isSystem', '==', true),
        where('type', '==', 'template'),
        limit(200),
      ),
    );
  }

  const fetched: SystemTemplateDoc[] = [];
  sysSnap.forEach((docSnap) => {
    const data = docSnap.data();
    if (!data.templateCaseTypes?.length) {
      data.templateCaseTypes = inferTemplateCaseTypes({
        title: data.title,
        templateStagesByCaseType: data.templateStagesByCaseType,
        documentType: data.documentType,
      });
    }
    if (!data.templateSides?.length && data.title) {
      const inferred = inferTemplateSides(getDisplayTemplateTitle({
        id: docSnap.id,
        title: data.title,
        tabs: data.tabs,
      }));
      if (inferred.length) data.templateSides = inferred;
    }
    fetched.push({ id: docSnap.id, ...data } as SystemTemplateDoc);
  });
  return filterActive(fetched);
}

export async function fetchSystemTemplates(): Promise<SystemTemplateDoc[]> {
  const fetched = (await fetchAllSystemTemplateDocs()).map(normalizeTemplateRecord);
  fetched.sort((a, b) =>
    getDisplayTemplateTitle(a).localeCompare(getDisplayTemplateTitle(b), undefined, {
      sensitivity: 'base',
    }),
  );
  return fetched;
}

export function getSystemTemplatesForCaseRecommendations(
  caseData: Record<string, unknown> | null | undefined,
  systemTemplates: SystemTemplateDoc[],
): SystemTemplateDoc[] {
  const deduped = dedupeSystemTemplatesByTitle(systemTemplates);
  return deduped
    .filter((t) => templateMatchesCaseForRecommendations(t, caseData))
    .sort((a, b) =>
      getDisplayTemplateTitle(a).localeCompare(getDisplayTemplateTitle(b), undefined, {
        sensitivity: 'base',
      }),
    );
}

/** Stable editor URL for a LexGat system template (uses Firestore doc id, not display title). */
export function lexGatSystemTemplateEditPath(docId: string): string {
  return `/drafts/system/${encodeURIComponent(docId)}`;
}

/**
 * Resolve canonical SYSTEM template from an in-memory list (no Firestore round-trip).
 * Use when templates were already loaded (e.g. Recommended Drafts tab).
 */
export function resolveCanonicalSystemTemplateFromCache(
  idOrTitle: string,
  cachedTemplates: SystemTemplateDoc[],
): SystemTemplateDoc | null {
  const trimmed = decodeURIComponent(idOrTitle).trim();
  if (!trimmed || cachedTemplates.length === 0) return null;

  const all = cachedTemplates.map(normalizeTemplateRecord);
  const exact = all.find((t) => t.id === trimmed);
  if (exact) return normalizeTemplateRecord(exact);

  const probe = { id: trimmed, title: trimmed };
  const norm = normalizeTemplateTitle(trimmed);
  const cluster = all.filter(
    (t) =>
      normalizeTemplateTitle(getDisplayTemplateTitle(t)) === norm ||
      templatesAreLogicalDuplicates(t, probe),
  );

  if (cluster.length === 0) return null;
  return normalizeTemplateRecord(cluster.reduce(preferTemplateRecord));
}

/**
 * Resolve the same canonical SYSTEM template document used everywhere
 * (admin library editor + Recommended Drafts). Picks richest content among true duplicates only.
 */
export async function resolveCanonicalSystemTemplate(
  idOrTitle: string,
): Promise<SystemTemplateDoc | null> {
  const trimmed = decodeURIComponent(idOrTitle).trim();
  if (!trimmed) return null;

  const all = (await fetchAllSystemTemplateDocs()).map(normalizeTemplateRecord);
  const fromCache = resolveCanonicalSystemTemplateFromCache(trimmed, all);
  if (fromCache) {
    const fresh = await fetchSystemTemplateFresh(fromCache.id);
    return fresh?.isSystem === true
      ? normalizeTemplateRecord(fresh)
      : fromCache;
  }

  const byId = await fetchSystemTemplateFresh(trimmed);
  return byId?.isSystem === true ? normalizeTemplateRecord(byId) : null;
}

/** Load a LexGat system template for the editor by doc id or display title. */
export async function getLexGatSystemTemplateForEdit(
  idOrTitle: string,
): Promise<SystemTemplateDoc | null> {
  return resolveCanonicalSystemTemplate(idOrTitle);
}

/** Fetch the latest template body from Firestore (bypasses in-memory lists). */
export async function fetchSystemTemplateFresh(
  templateId: string,
  workspaceId = 'SYSTEM',
): Promise<SystemTemplateDoc | null> {
  const snap = await getDoc(
    doc(db, 'workspaces', workspaceId, 'documents', templateId),
  );
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as SystemTemplateDoc;
}

export function isSystemTemplateAdmin(email: string | null | undefined): boolean {
  return isAdminEmail(email);
}

/** Section headers for the LexGat templates library UI (only categories that have templates). */
export function getLexGatTemplateDisplayCategories(
  templates: SystemTemplateDoc[] = [],
): string[] {
  const fromCatalog = Object.keys(TEMPLATE_CATEGORIES);
  if (fromCatalog.length > 0) return fromCatalog;

  if (templates.length > 0) {
    const ids = new Set<string>();
    for (const t of templates) {
      for (const id of t.templateCaseTypes ?? []) {
        const resolved = resolveMatterTypeId(id);
        if (resolved && CASE_TYPE_KEYS.has(resolved)) ids.add(resolved);
        else if (CASE_TYPE_KEYS.has(id)) ids.add(id);
      }
      for (const rawKey of Object.keys(t.templateStagesByCaseType ?? {})) {
        const resolved = resolveMatterTypeId(rawKey);
        if (resolved && CASE_TYPE_KEYS.has(resolved)) ids.add(resolved);
        else if (CASE_TYPE_KEYS.has(rawKey)) ids.add(rawKey);
      }
    }
    if (ids.size > 0) {
      return [...ids].sort((a, b) =>
        getMatterTypeDisplayLabel(a).localeCompare(getMatterTypeDisplayLabel(b), undefined, {
          sensitivity: 'base',
        }),
      );
    }
  }

  return ['All Templates'];
}

/** Matter type ids assigned to a template (explicit tags + stage-map keys). */
export function resolveTemplateMatterTypeIds(
  tpl: Pick<SystemTemplateDoc, 'templateCaseTypes' | 'templateStagesByCaseType'>,
): string[] {
  const ids = new Set<string>();
  for (const raw of tpl.templateCaseTypes ?? []) {
    const resolved = resolveMatterTypeId(raw);
    if (resolved && CASE_TYPE_KEYS.has(resolved)) ids.add(resolved);
    else if (CASE_TYPE_KEYS.has(raw)) ids.add(raw);
  }
  for (const rawKey of Object.keys(tpl.templateStagesByCaseType ?? {})) {
    const resolved = resolveMatterTypeId(rawKey);
    if (resolved && CASE_TYPE_KEYS.has(resolved)) ids.add(resolved);
    else if (CASE_TYPE_KEYS.has(rawKey)) ids.add(rawKey);
  }
  return [...ids];
}

export function isLexGatTemplateUncategorized(
  tpl: Pick<SystemTemplateDoc, 'templateCaseTypes'>,
  categories: string[],
): boolean {
  const types = tpl.templateCaseTypes;
  if (!types?.length) return true;
  return !types.some((ct) => categories.includes(ct));
}

export type LexGatTemplateGroup = {
  category: string;
  templates: SystemTemplateDoc[];
  /** Criminal (and similar) nested proceeding buckets, e.g. Bail → BBA, BA, … */
  subsections?: LexGatTemplateGroup[];
};

export function lexGatTemplateGroupHasTemplates(group: LexGatTemplateGroup): boolean {
  if (group.templates.length > 0) return true;
  return (group.subsections ?? []).some(lexGatTemplateGroupHasTemplates);
}

export type LexGatTemplateTopGroup = {
  topCategory: string;
  sections: LexGatTemplateGroup[];
};

type TopCategoryFilter = Exclude<TopFilter, 'All' | 'Other'>;

const TOP_CATEGORY_GROUP_ORDER = TOP_FILTER_OPTIONS.filter(
  (t): t is TopCategoryFilter => t !== 'All' && t !== 'Other',
);

function sortTemplatesByTitle(templates: SystemTemplateDoc[]): SystemTemplateDoc[] {
  return [...templates].sort((a, b) =>
    getDisplayTemplateTitle(a).localeCompare(getDisplayTemplateTitle(b), undefined, {
      sensitivity: 'base',
    }),
  );
}

/** Keep each "(Copy)" template immediately after its source in grid lists. */
export function sortTemplatesWithCopyBesideSource(
  templates: SystemTemplateDoc[],
): SystemTemplateDoc[] {
  const roots: SystemTemplateDoc[] = [];
  const copies: SystemTemplateDoc[] = [];

  for (const tpl of templates) {
    if (isTemplateCopyTitle(getDisplayTemplateTitle(tpl))) copies.push(tpl);
    else roots.push(tpl);
  }

  const copyBaseKey = (title: string) =>
    title.replace(TEMPLATE_COPY_TITLE_SUFFIX, '').trim().toLowerCase();

  const result: SystemTemplateDoc[] = [];
  for (const root of sortTemplatesByTitle(roots)) {
    result.push(root);
    const base = copyBaseKey(getDisplayTemplateTitle(root));
    const related = sortTemplatesByTitle(
      copies.filter((c) => copyBaseKey(getDisplayTemplateTitle(c)) === base),
    );
    result.push(...related);
  }

  const placed = new Set(result.map((t) => t.id));
  const orphans = sortTemplatesByTitle(copies.filter((c) => !placed.has(c.id)));
  result.push(...orphans);
  return result;
}

/** Resolve top category-bar buckets (Civil, Criminal, Family, …) for a template. */
export function resolveTemplateTopCategories(
  tpl: Pick<SystemTemplateDoc, 'templateCaseTypes' | 'templateStagesByCaseType'>,
): TopFilter[] {
  const matterIds = resolveTemplateMatterTypeIds(tpl);
  const tops = new Set<TopFilter>();
  for (const id of matterIds) {
    const caseCategory = inferCategoryFromMatterType(id);
    if (!caseCategory) continue;
    const top = CASE_CATEGORY_TO_TOP_FILTER[caseCategory];
    if (top && top !== 'All' && top !== 'Other') tops.add(top);
  }
  return [...tops];
}

function resolveTemplateCriminalProceedingId(
  tpl: Pick<SystemTemplateDoc, 'templateCaseTypes' | 'templateStagesByCaseType'>,
): string | undefined {
  for (const raw of resolveTemplateMatterTypeIds(tpl)) {
    const resolved = resolveMatterTypeId(raw);
    const candidates = [raw, resolved].filter(Boolean);
    for (const id of candidates) {
      const proceeding = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, id);
      if (proceeding) return proceeding.id;
      const legacy =
        resolveLegacyCriminalProceeding(id) ??
        LEGACY_CRIMINAL_TYPE_MAP[id] ??
        LEGACY_CRIMINAL_TYPE_MAP[id.toLowerCase()];
      if (legacy?.proceedingId) return legacy.proceedingId;
    }
  }
  return undefined;
}

function getCriminalProceedingSectionLabel(proceedingId: string): string {
  const proceeding = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, proceedingId);
  return proceeding?.shortLabel?.trim() || proceeding?.label || proceedingId;
}

function groupCriminalTemplatesByProceeding(
  templates: SystemTemplateDoc[],
  criminalCategoryId: string,
): LexGatTemplateGroup[] {
  if (templates.length === 0) return [];

  const selectableIds = new Set(
    getSelectableProceedingsForCategory(CRIMINAL_CASE_TAXONOMY_CONFIG, criminalCategoryId).map(
      (p) => p.id,
    ),
  );
  const proceedings = getProceedingsForCategory(CRIMINAL_CASE_TAXONOMY_CONFIG, criminalCategoryId);
  const orderedProceedingIds = [
    ...proceedings.filter((p) => selectableIds.has(p.id)).map((p) => p.id),
    ...proceedings.filter((p) => !selectableIds.has(p.id)).map((p) => p.id),
  ];

  const assigned = new Set<string>();
  const subsections: LexGatTemplateGroup[] = [];

  for (const proceedingId of orderedProceedingIds) {
    const items = templates.filter((t) => {
      if (assigned.has(t.id)) return false;
      return resolveTemplateCriminalProceedingId(t) === proceedingId;
    });
    if (items.length === 0) continue;
    items.forEach((t) => assigned.add(t.id));
    subsections.push({
      category: getCriminalProceedingSectionLabel(proceedingId),
      templates: sortTemplatesWithCopyBesideSource(items),
    });
  }

  const remaining = templates.filter((t) => !assigned.has(t.id));
  if (remaining.length > 0) {
    subsections.push({
      category: 'Other',
      templates: sortTemplatesWithCopyBesideSource(remaining),
    });
  }

  return subsections;
}

/**
 * Group templates under sub-filter bar headings (Suits, Appeals, …) for one top
 * category. Order and labels match CaseCategoryFilter sub-chips.
 * Criminal adds a third level: proceeding types (BBA, BA, Sessions, …).
 */
export function groupLexGatTemplatesForTopFilter(
  templates: SystemTemplateDoc[],
  topFilter: TopCategoryFilter,
): LexGatTemplateGroup[] {
  if (templates.length === 0) return [];

  const subLabels = SUBFILTER_OPTIONS[topFilter].slice(1);
  const assigned = new Set<string>();
  const groups: LexGatTemplateGroup[] = [];

  for (const subLabel of subLabels) {
    const items = templates.filter((t) => {
      if (assigned.has(t.id)) return false;
      return matchesTemplateTaxonomy(
        resolveTemplateMatterTypeIds(t),
        topFilter,
        subLabel,
      );
    });
    if (items.length === 0) continue;
    items.forEach((t) => assigned.add(t.id));

    if (topFilter === 'Criminal') {
      const criminalCategoryId = getCriminalCategoryIdForSubFilter(subLabel);
      const subsections = criminalCategoryId
        ? groupCriminalTemplatesByProceeding(items, criminalCategoryId)
        : [];
      groups.push({
        category: subLabel,
        templates: subsections.length > 0 ? [] : sortTemplatesWithCopyBesideSource(items),
        subsections: subsections.length > 0 ? subsections : undefined,
      });
    } else {
      groups.push({
        category: subLabel,
        templates: sortTemplatesWithCopyBesideSource(items),
      });
    }
  }

  const remaining = templates.filter((t) => !assigned.has(t.id));
  if (remaining.length > 0) {
    groups.push({
      category: 'Uncategorized / Other',
      templates: sortTemplatesWithCopyBesideSource(remaining),
    });
  }

  return groups;
}

/** Matter-type sections within one top category (Civil, Criminal, …). */
function groupTemplatesByMatterTypeInTop(
  templates: SystemTemplateDoc[],
  top: TopCategoryFilter,
): LexGatTemplateGroup[] {
  return groupLexGatTemplatesForTopFilter(templates, top);
}

/**
 * Group templates under category-bar headings (Civil, Criminal, Family, …),
 * each with matter-type sub-sections. Used when the library filter is "All".
 */
export function groupLexGatTemplatesByTopCategoryWithMatterTypes(
  templates: SystemTemplateDoc[],
): LexGatTemplateTopGroup[] {
  if (templates.length === 0) return [];

  const assigned = new Set<string>();
  const result: LexGatTemplateTopGroup[] = [];

  for (const top of TOP_CATEGORY_GROUP_ORDER) {
    const items = templates.filter((t) => {
      if (assigned.has(t.id)) return false;
      return resolveTemplateTopCategories(t).includes(top);
    });
    if (items.length === 0) continue;
    items.forEach((t) => assigned.add(t.id));
    result.push({
      topCategory: top,
      sections: groupTemplatesByMatterTypeInTop(items, top),
    });
  }

  const remaining = templates.filter((t) => !assigned.has(t.id));
  if (remaining.length > 0) {
    result.push({
      topCategory: 'Other',
      sections: [
        {
          category: 'Uncategorized / Other',
          templates: sortTemplatesByTitle(remaining),
        },
      ],
    });
  }

  return result;
}

/** @deprecated Prefer groupLexGatTemplatesByTopCategoryWithMatterTypes for the All filter. */
export function groupLexGatTemplatesByTopCategory(
  templates: SystemTemplateDoc[],
): LexGatTemplateGroup[] {
  return groupLexGatTemplatesByTopCategoryWithMatterTypes(templates).flatMap(
    (topGroup) =>
      topGroup.sections.map((section) => ({
        category:
          topGroup.topCategory === section.category
            ? topGroup.topCategory
            : `${topGroup.topCategory} — ${section.category}`,
        templates: section.templates,
      })),
  );
}

/**
 * Group LexGat templates for the library grid. Every template appears once:
 * under the first matching case-type category, or under "Uncategorized / Other".
 */
export function groupLexGatTemplatesForDisplay(
  templates: SystemTemplateDoc[],
  categories: string[] = getLexGatTemplateDisplayCategories(templates),
): LexGatTemplateGroup[] {
  if (categories.length === 1 && categories[0] === 'All Templates') {
    return groupLexGatTemplatesByTopCategoryWithMatterTypes(templates).flatMap(
      (g) => g.sections,
    );
  }

  const assigned = new Set<string>();
  const groups: LexGatTemplateGroup[] = [];

  for (const category of categories) {
    const items = templates.filter((t) => {
      if (assigned.has(t.id)) return false;
      const types = t.templateCaseTypes ?? [];
      return types.some((typeId) => resolveMatterTypeId(typeId) === category || typeId === category);
    });
    if (items.length === 0) continue;
    items.forEach((t) => assigned.add(t.id));
    groups.push({
      category,
      templates: sortTemplatesWithCopyBesideSource(items),
    });
  }

  const remaining = templates.filter((t) => !assigned.has(t.id));
  if (remaining.length > 0) {
    groups.push({
      category: 'Uncategorized / Other',
      templates: sortTemplatesWithCopyBesideSource(remaining),
    });
  }

  return groups;
}

/** Same grouping for workspace custom templates on the Templates tab */
export function groupWorkspaceTemplatesForDisplay(
  templates: Array<{ id: string; templateCaseTypes?: string[] } & Record<string, unknown>>,
  categories: string[] = getLexGatTemplateDisplayCategories(
    templates as SystemTemplateDoc[],
  ),
): Array<{ category: string; templates: typeof templates }> {
  if (categories.length === 1 && categories[0] === 'All Templates') {
    if (templates.length === 0) return [];
    return [{ category: 'All Templates', templates: [...templates] }];
  }

  const assigned = new Set<string>();
  const groups: Array<{ category: string; templates: typeof templates }> = [];

  for (const category of categories) {
    const items = templates.filter((t) => {
      if (assigned.has(t.id)) return false;
      const types = t.templateCaseTypes ?? [];
      return types.some((typeId) => resolveMatterTypeId(typeId) === category || typeId === category);
    }) as typeof templates;
    if (items.length === 0) continue;
    items.forEach((t) => assigned.add(t.id));
    groups.push({ category, templates: items });
  }

  const remaining = templates.filter((t) => !assigned.has(t.id));
  if (remaining.length > 0) {
    groups.push({ category: 'Uncategorized / Other', templates: remaining });
  }

  return groups;
}

/** Firestore document id for a LexGat system template from its title */
export function systemTemplateDocId(title: string): string {
  const norm = normalizeTemplateTitle(title);
  const slugBody = slugBodyFromTitle(title);
  const full = `system-template-${slugBody}`;
  if (slugBody.length > 0 && full.length <= 120) return full;
  return `system-template-${hashTitleKey(norm)}`;
}

export function normalizeTemplateTitle(title: string): string {
  return title.trim().toLowerCase();
}

export function lexGatTemplateTitleExists(
  title: string,
  systemTemplates: Pick<SystemTemplateDoc, 'title'>[],
): boolean {
  const norm = normalizeTemplateTitle(title);
  return systemTemplates.some(
    (t) => normalizeTemplateTitle(t.title || '') === norm,
  );
}

/** Unique title for a duplicated LexGat library template. */
export function suggestLexGatTemplateDuplicateTitle(
  baseTitle: string,
  systemTemplates: Pick<SystemTemplateDoc, 'title'>[],
): string {
  const root =
    baseTitle.replace(/\s*\(copy(?:\s+\d+)?\)\s*$/i, '').trim() || baseTitle;
  for (let n = 0; n < 100; n++) {
    const candidate =
      n === 0 ? `${root} (Copy)` : `${root} (Copy ${n + 1})`;
    if (!lexGatTemplateTitleExists(candidate, systemTemplates)) {
      return candidate;
    }
  }
  return `${root} (Copy ${Date.now()})`;
}

export function isWorkspaceTemplatePublishedToLexGat(
  workspaceTitle: string,
  systemTemplates: Pick<SystemTemplateDoc, 'title'>[],
): boolean {
  return lexGatTemplateTitleExists(workspaceTitle, systemTemplates);
}

export type LexGatTemplateUpsertInput = {
  title: string;
  content?: string;
  tabs?: SystemTemplateDoc['tabs'];
  activeTabId?: string;
  templateCaseTypes?: string[];
  templateSides?: string[];
  templateStagesByCaseType?: TemplateStagesByCaseType;
  documentType?: string;
};

export function buildLexGatTemplateFirestoreData(input: LexGatTemplateUpsertInput) {
  return {
    title: input.title.trim(),
    content: input.content ?? '',
    ...(input.tabs ? { tabs: input.tabs, activeTabId: input.activeTabId } : {}),
    ...(input.templateCaseTypes !== undefined
      ? { templateCaseTypes: input.templateCaseTypes }
      : {}),
    ...(input.templateSides !== undefined ? { templateSides: input.templateSides } : {}),
    ...(input.templateStagesByCaseType !== undefined
      ? { templateStagesByCaseType: input.templateStagesByCaseType }
      : {}),
    ...(input.documentType ? { documentType: input.documentType } : {}),
    type: 'template',
    isSystem: true,
    workspaceId: 'SYSTEM',
    lawyerId: 'SYSTEM',
    updatedAt: serverTimestamp(),
  };
}

export async function isLexGatTemplateDocIdTaken(
  title: string,
  excludeDocId?: string,
): Promise<boolean> {
  const docId = systemTemplateDocId(title);
  if (excludeDocId && docId === excludeDocId) return false;
  const snap = await getDoc(doc(db, 'workspaces', 'SYSTEM', 'documents', docId));
  return snap.exists();
}

export async function upsertLexGatSystemTemplate(
  input: LexGatTemplateUpsertInput,
  targetDocId?: string,
): Promise<string> {
  const normalized = normalizeTemplateSavePayload(input);
  const sysId = targetDocId?.trim() || systemTemplateDocId(normalized.title);
  const ref = doc(db, 'workspaces', 'SYSTEM', 'documents', sysId);
  await setDoc(ref, buildLexGatTemplateFirestoreData(normalized));
  return sysId;
}

async function deleteSingleLexGatSystemTemplate(
  templateId: string,
  deletedBy?: string,
): Promise<void> {
  const ref = doc(db, 'workspaces', 'SYSTEM', 'documents', templateId);
  if (deletedBy) {
    const snap = await getDoc(ref);
    const title = snap.exists()
      ? getDisplayTemplateTitle({ id: templateId, ...(snap.data() as object) } as SystemTemplateDoc)
      : templateId;
    await softDelete(ref, {
      deletedBy,
      entityType: 'template',
      title,
    });
    return;
  }
  await deleteDoc(ref);
}

/** Save LexGat template to the doc being edited; migrate doc id only when the title changes. */
export async function saveLexGatSystemTemplate(
  input: LexGatTemplateUpsertInput,
  options?: { previousDocId?: string | null; lockDocId?: boolean },
): Promise<{ docId: string; renamed: boolean }> {
  const normalized = normalizeTemplateSavePayload(input);
  const canonicalId = systemTemplateDocId(normalized.title);
  const previousDocId = options?.previousDocId?.trim() || null;

  let targetDocId = previousDocId || canonicalId;
  let renamed = false;
  if (options?.lockDocId && previousDocId) {
    targetDocId = previousDocId;
  } else if (previousDocId && canonicalId !== previousDocId) {
    targetDocId = canonicalId;
    renamed = true;
  }

  const newDocId = await upsertLexGatSystemTemplate(input, targetDocId);

  if (renamed && previousDocId && previousDocId !== newDocId) {
    await deleteSingleLexGatSystemTemplate(previousDocId);
  }

  return { docId: newDocId, renamed };
}

function buildDuplicateTemplatePayload(
  source: SystemTemplateDoc,
  newTitle: string,
): LexGatTemplateUpsertInput {
  const resolved = resolveTemplateContent(source);
  const tabs = resolved.tabs?.map((tab) => ({
    id: tab.id,
    title: tab.title,
    content: tab.content ?? '',
  }));

  return {
    title: newTitle,
    content: resolved.html,
    ...(tabs?.length
      ? { tabs, activeTabId: resolved.activeTabId ?? tabs[0]?.id }
      : {}),
    templateCaseTypes: source.templateCaseTypes
      ? [...source.templateCaseTypes]
      : undefined,
    templateSides: source.templateSides ? [...source.templateSides] : undefined,
    templateStagesByCaseType: source.templateStagesByCaseType
      ? structuredClone(source.templateStagesByCaseType)
      : undefined,
    documentType: source.documentType,
  };
}

function allocateUniqueSystemTemplateDocIdFromList(
  title: string,
  knownTemplates: Pick<SystemTemplateDoc, 'id'>[],
): string {
  const existingIds = new Set(knownTemplates.map((t) => t.id));
  const baseDocId = systemTemplateDocId(title);
  let docId = baseDocId;
  let n = 0;
  while (existingIds.has(docId)) {
    n += 1;
    const suffix = `-copy-${n}`;
    docId =
      baseDocId.length + suffix.length <= 120
        ? `${baseDocId}${suffix}`
        : `${baseDocId.slice(0, 120 - suffix.length)}${suffix}`;
  }
  return docId;
}

export type PreparedLexGatTemplateDuplicate = {
  docId: string;
  title: string;
  payload: LexGatTemplateUpsertInput;
  template: SystemTemplateDoc;
};

/** Build duplicate in memory (no network). */
export function prepareLexGatTemplateDuplicate(
  source: SystemTemplateDoc,
  systemTemplates: SystemTemplateDoc[],
): PreparedLexGatTemplateDuplicate {
  const canonical = normalizeTemplateRecord(source);
  const displayTitle = getDisplayTemplateTitle(canonical);
  const newTitle = suggestLexGatTemplateDuplicateTitle(
    displayTitle,
    systemTemplates,
  );
  const payload = buildDuplicateTemplatePayload(canonical, newTitle);
  const docId = allocateUniqueSystemTemplateDocIdFromList(newTitle, systemTemplates);
  const template = normalizeTemplateRecord({
    id: docId,
    ...payload,
    type: 'template',
    isSystem: true,
    workspaceId: 'SYSTEM',
    lawyerId: 'SYSTEM',
  } as SystemTemplateDoc);

  return { docId, title: newTitle, payload, template };
}

export async function persistLexGatTemplateDuplicate(
  prepared: PreparedLexGatTemplateDuplicate,
): Promise<void> {
  await upsertLexGatSystemTemplate(prepared.payload, prepared.docId);
}

async function resolveDuplicateSource(
  source: SystemTemplateDoc,
): Promise<SystemTemplateDoc> {
  const normalized = normalizeTemplateRecord(source);
  if (templateHasFullContent(normalized)) return normalized;
  const fresh = await fetchSystemTemplateFresh(source.id);
  if (fresh) return normalizeTemplateRecord(fresh);
  const resolved = await resolveCanonicalSystemTemplate(source.id);
  if (!resolved) throw new Error('Template not found');
  return normalizeTemplateRecord(resolved);
}

/** Clone a LexGat system template into a new library document with a unique title. */
export async function duplicateLexGatSystemTemplate(
  source: SystemTemplateDoc,
  systemTemplates: SystemTemplateDoc[],
): Promise<{ docId: string; title: string; template: SystemTemplateDoc }> {
  const canonical = await resolveDuplicateSource(source);
  const prepared = prepareLexGatTemplateDuplicate(canonical, systemTemplates);
  await persistLexGatTemplateDuplicate(prepared);
  return {
    docId: prepared.docId,
    title: prepared.title,
    template: prepared.template,
  };
}

/** Insert a duplicated template in list UI directly after its source card. */
export function insertTemplateBesideSource(
  templates: SystemTemplateDoc[],
  copy: SystemTemplateDoc,
  sourceId: string,
): SystemTemplateDoc[] {
  const withoutCopy = templates.filter((t) => t.id !== copy.id);
  const sourceIndex = withoutCopy.findIndex((t) => t.id === sourceId);
  if (sourceIndex < 0) {
    return sortTemplatesWithCopyBesideSource([...withoutCopy, copy]);
  }
  const next = [...withoutCopy];
  next.splice(sourceIndex + 1, 0, copy);
  return next;
}

/** Remove one LexGat system template document by id. */
export async function deleteLexGatSystemTemplate(
  template: Pick<SystemTemplateDoc, 'id' | 'title'>,
  deletedBy?: string,
): Promise<void> {
  await deleteSingleLexGatSystemTemplate(template.id, deletedBy);
}
